import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { DIFF_STATUS_CFG, DIFF_STATUS_ORDER } from '~/constants/diff';
import type { DiffStatus } from '~/types/product-diff';
import { cn } from '~/utils/cn';

interface Props {
  /** Display name — already resolved, so `Not carried over` arrives translated. */
  label: string;
  rowCount: number;
  counts: Record<DiffStatus, number>;
  isOpen: boolean;
  onToggle: () => void;
  /** `id` of the row region this band controls, for `aria-controls`. */
  regionId: string;
  /** Compact per-section status tally on the right. Off for the `panels`
   *  layout's current-side band, where the optimized side already carries it and
   *  repeating it in both columns says the same thing twice. */
  showLedger?: boolean;
  /** `data-testid` for the band button; derives `-label`, `-count`, `-ledger-{status}`. */
  testId?: string;
}

/**
 * Section divider spanning the row surface: chevron, section name, attribute
 * count, and a right-aligned ledger of per-section status counts.
 *
 * Deliberately a divider and not a card — no nested border, shadow or radius,
 * just a background one step darker than the rows and a hairline underneath. It
 * has to sit a level *quieter* than the "Product Details" heading above it, or it
 * competes with the page's own section hierarchy.
 */
export function AttributeSectionBand({
  label,
  rowCount,
  counts,
  isOpen,
  onToggle,
  regionId,
  showLedger = true,
  testId = 'attribute-section-band',
}: Props) {
  const { t } = useTranslation('common');

  return (
    <Button
      type="button"
      variant="ghost"
      aria-expanded={isOpen}
      aria-controls={regionId}
      data-testid={testId}
      title={
        isOpen
          ? t('productDetailsComparison.collapseSection', { section: label })
          : t('productDetailsComparison.expandSection', { section: label })
      }
      onClick={onToggle}
      // Resets the Button chrome down to a band: full width, square corners, and
      // its own hairline instead of the variant's padding and radius. A step darker
      // than any row wash so it still reads as a divider between an untinted
      // `unchanged` row and the tinted ones on either side of it.
      className="bg-neutral-20 hover:bg-neutral-30 h-auto w-full cursor-pointer justify-start gap-2.5 rounded-none border-b px-4 py-2 text-left"
    >
      <ChevronDown
        className={cn(
          'text-muted-foreground size-3.5 shrink-0 transition-transform motion-reduce:transition-none',
          // Rotation only — animating height or margin here would shift every
          // row below the band on each toggle.
          isOpen ? '' : '-rotate-90',
        )}
        aria-hidden="true"
      />
      <span
        className="text-secondary-text text-[11.5px] font-semibold tracking-[0.14em] uppercase"
        data-testid={`${testId}-label`}
      >
        {label}
      </span>{' '}
      <span
        className="text-muted-foreground text-[11.5px] font-normal"
        data-testid={`${testId}-count`}
      >
        {t('productDetailsComparison.sectionCount', { count: rowCount })}
      </span>
      {showLedger && (
        <span className="ml-auto flex items-center gap-1">
          {/* Changes only. `unchanged` is a non-event, and its `=1` chip both read
              as a riddle and restated the attribute count sitting beside it. */}
          {DIFF_STATUS_ORDER.filter((status) => status !== 'unchanged' && counts[status] > 0).map(
            (status) => {
              const cfg = DIFF_STATUS_CFG[status];
              const label_ = t(`productDetailsComparison.status.${cfg.labelKey}`);
              return (
                <span
                  key={status}
                  title={label_}
                  data-testid={`${testId}-ledger-${status}`}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[11px] leading-4 font-semibold',
                    cfg.badge,
                  )}
                >
                  <span aria-hidden="true">{cfg.sign}</span>
                  {counts[status]}
                  {/* The sign is decoration; the status name is the fact. */}
                  <span className="sr-only">{label_}</span>
                </span>
              );
            },
          )}
        </span>
      )}
    </Button>
  );
}
